// Which kind of problem a refusal was, in the person's words (ai_app_backend #1678, its PR #1689). When v2's safety
// check refuses words it names one fixed public code, with `public_only` (true when a picture may not be published but
// may stay private, false otherwise):
// - `422 words_refused` names it as `reason`;
// - `409 keep_refused` and `publish_refused` already had `reason: "words"` (refused for its words, beside
//   `early_access`, `age_check`, `feed_rule` …), so they name it as `words_reason`; their `reason` is never a code.
// An adapter carries the code onto the v1-shaped answer it makes as `reason` (`refusalFields`), which the screens word
// through `t()` (`refusalLine`); nothing else of the refusal is shown. A `keep_refused` also carries `unsaved: true`:
// a picture that couldn't be kept stays unsaved, so the line says it can still be downloaded (Ruben, 5 Nov 2026: the
// old "This can't be kept: it goes when its 24 hours are up" read like a bug). The server decides. No code (an older v2, a
// listed word, a folder or persona check, a chat worker's refusal, a picture refused before) leaves each screen as
// before.

/** The codes v2 may send as a refusal's `reason`; any other value is worded as `other`. */
export const REFUSAL_REASONS = [
  'minors',
  'juvenile_settings',
  'real_people',
  'extreme_violence',
  'named_characters',
  'illegal_drugs',
  'sexual_violence',
  'animals',
  'incest',
  'bodily_waste',
  'hate',
  'other',
] as const

export type RefusalReason = (typeof REFUSAL_REASONS)[number]

/** The translation key of each reason's line (`en` holds the English). */
export const REASON_KEYS: Record<RefusalReason, string> = {
  minors: 'refusalMinors',
  // A school setting at high-school level or below, or something childlike, whatever age is stated (ai_app_backend
  // #2118): its own line, never `minors`'s.
  juvenile_settings: 'refusalJuvenileSettings',
  real_people: 'refusalRealPeople',
  extreme_violence: 'refusalExtremeViolence',
  named_characters: 'refusalNamedCharacters',
  illegal_drugs: 'refusalIllegalDrugs',
  sexual_violence: 'refusalSexualViolence',
  animals: 'refusalAnimals',
  incest: 'refusalIncest',
  bodily_waste: 'refusalBodilyWaste',
  hate: 'refusalHate',
  other: 'refusalOther',
}

/** What follows the reason: "Change your prompt" where a prompt was refused, "Change it" for a chat or character. */
export type RefusalFix = 'prompt' | 'it'

const FIX_KEYS: Record<RefusalFix, string> = {
  prompt: 'refusalChangePrompt',
  it: 'refusalChangeIt',
}

/**
 * A tip said instead of "Change your prompt" for the reasons where one helps more (Ruben, 5 Nov 2026): an age
 * refusal is most often fixed by giving everyone an adult age. Only the reason and one line are ever shown.
 */
export const TIP_KEYS: Partial<Record<RefusalReason, string>> = {
  minors: 'refusalAdultAgeTip',
}

/** What follows the reason when keeping was refused: the picture stays unsaved, so it can still be downloaded. */
export const STILL_DOWNLOAD_KEY = 'refusalStillDownload'

/** The line said first when a picture may not be published but may be kept private. */
export const PUBLIC_ONLY_KEY = 'refusalPublicOnly'

/** The words of the button that keeps such a picture private instead. */
export const KEEP_PRIVATE_KEY = 'refusalKeepPrivate'

type T = (key: string, options?: Record<string, unknown>) => string

/**
 * A word list's refusal names the words it found (ai_app_backend #2148; Ruben, 5 Nov 2026): `words`, at most 5, as
 * typed, on a `words_refused` or a `keep_refused`, or carried by an adapter. Never the floor list's words, an age or
 * the AI judge's (those send none). The site never guesses them.
 */
export const NAMED_WORDS_KEY = 'refusalNamedWords'

/** What follows the named words: take them out. */
export const REMOVE_WORDS_KEY = 'refusalRemoveWords'

/** The most words a refusal names (v2 sends no more). */
const MOST_WORDS = 5

const KNOWN = new Set<string>(REFUSAL_REASONS)

type Fields = {
  code?: unknown
  reason?: unknown
  words_reason?: unknown
  public_only?: unknown
  unsaved?: unknown
  words?: unknown
}

const fieldsOf = (body: unknown): Fields =>
  typeof body === 'object' && body !== null ? (body as Fields) : {}

/**
 * The `reason`s v1 itself sends on a refused save, publish or `/api/generate` (promptchan-backend #371,
 * `save_media.REFUSED_*`, 5 Nov 2026), with no `code`: which check refused it, never the judge's category. The screen
 * says v1's own words for these (and offers v1's slider reset), as before #371.
 */
const V1_CHECKS = new Set([
  'words',
  'age_check',
  'content_guidelines',
  'sliders',
  'early_access',
  'unknown',
])

/**
 * The judge's code as sent, or null: `words_reason` when v2 sends it (a refusal to keep or publish); otherwise `reason`
 * on a `words_refused`, or on an adapter's v1-shaped answer (no `code`) that carries it. Any other refusal's `reason`
 * is its own word (`keep_refused`'s `words` or `early_access`, `reference_refused`'s `no_face` …, v1's `V1_CHECKS`),
 * never a code.
 */
function codeOf(body: unknown): string | null {
  const { code, reason, words_reason } = fieldsOf(body)
  if (typeof words_reason === 'string' && words_reason) return words_reason
  if (code !== undefined && code !== 'words_refused') return null
  return typeof reason === 'string' && reason && !V1_CHECKS.has(reason)
    ? reason
    : null
}

/** The reason a refusal names, as one of the codes worded here (an unknown one is `other`), or null when it names none. */
export function refusalReason(body: unknown): RefusalReason | null {
  const reason = codeOf(body)
  if (!reason) return null
  return KNOWN.has(reason) ? (reason as RefusalReason) : 'other'
}

/**
 * The words a word list's refusal names, as sent (at most 5, each a non-empty string), or null when it names none:
 * only on a `words_refused`, a `keep_refused` or an adapter's answer (no `code`).
 */
export function namedWords(body: unknown): string[] | null {
  const { code, words } = fieldsOf(body)
  if (code !== undefined && code !== 'words_refused' && code !== 'keep_refused')
    return null
  if (!Array.isArray(words)) return null
  const named = words
    .filter((word): word is string => typeof word === 'string' && word.trim() !== '')
    .slice(0, MOST_WORDS)
  return named.length ? named : null
}

/**
 * Whether keeping was refused, so the picture stays unsaved (and can be downloaded until it expires): v2's
 * `keep_refused`, or an adapter's answer that carries `unsaved: true`. A refused publish says nothing of this: the
 * picture may already be kept.
 */
const stillUnsaved = (body: unknown) => {
  const { code, unsaved } = fieldsOf(body)
  return code === 'keep_refused' || unsaved === true
}

/** Whether the refusal is for publishing only: the picture may stay private (never when keeping itself was refused). */
export const isPublicOnly = (body: unknown) =>
  refusalReason(body) !== null &&
  fieldsOf(body).public_only === true &&
  !stillUnsaved(body)

/**
 * The fields of v2's refusal a screen words, to carry onto the v1-shaped answer an adapter makes: the judge's code as
 * `reason` (`words_refused`'s `reason`, or keep's and publish's `words_reason`), `public_only` when true, and
 * `unsaved` when keeping was refused; nothing when v2 named no code, so an older v2's answer is unchanged.
 */
export function refusalFields(body: unknown): {
  reason?: string
  words?: string[]
  public_only?: true
  unsaved?: true
} {
  const reason = codeOf(body)
  const words = namedWords(body)
  if (!reason && !words) return {}
  return {
    ...(reason ? { reason } : {}),
    ...(words ? { words } : {}),
    ...(isPublicOnly(body) ? { public_only: true as const } : {}),
    ...(stillUnsaved(body) ? { unsaved: true as const } : {}),
  }
}

/** Stands for the words while the sentence is translated, so a screen can set them in bold wherever they fall. */
const WORDS_MARK = '\u0000words\u0000'

/**
 * A refusal that names its words, in parts: the sentence around the words (`before`, `after`, translated by `t`), the
 * words themselves (comma-separated, as typed), and the line that follows (`next`): remove them, or, when keeping was
 * refused, that it can still be downloaded. Null when it names none.
 */
export function namedWordsParts(
  t: T,
  body: unknown,
): { before: string; words: string; after: string; next: string } | null {
  const named = namedWords(body)
  if (!named) return null
  const sentence = t(NAMED_WORDS_KEY, { words: WORDS_MARK })
  const at = sentence.indexOf(WORDS_MARK)
  const [before, after] =
    at === -1 ? [sentence, ''] : [sentence.slice(0, at), sentence.slice(at + WORDS_MARK.length)]
  return {
    before,
    words: named.join(', '),
    after,
    next: t(stillUnsaved(body) ? STILL_DOWNLOAD_KEY : REMOVE_WORDS_KEY),
  }
}

/**
 * The words a screen shows for a refusal that names its reason, translated by `t`, or null when it names none (the
 * screen then says what it said before). Named words come first: "These words can't be used here: …" and to remove
 * them (or, for a refused keep, that it can still be downloaded). Otherwise the reason and one line: a refused keep says why, then that it can
 * still be downloaded; a publish-only refusal says the picture may be kept private, then why; any other says why, then
 * its tip (`TIP_KEYS`) or else what to change (`fix`).
 */
export function refusalLine(
  t: T,
  body: unknown,
  fix: RefusalFix = 'prompt',
): string | null {
  const named = namedWordsParts(t, body)
  if (named) return `${named.before}${named.words}${named.after} ${named.next}`
  const reason = refusalReason(body)
  if (!reason) return null
  const why = t(REASON_KEYS[reason])
  if (stillUnsaved(body)) return `${why} ${t(STILL_DOWNLOAD_KEY)}`
  if (isPublicOnly(body)) return `${t(PUBLIC_ONLY_KEY)} ${why}`
  return `${why} ${t(TIP_KEYS[reason] ?? FIX_KEYS[fix])}`
}
